import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, ClipboardCheck, Lightbulb, TrendingDown, TrendingUp, Trash2,
} from "lucide-react";

import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { ShareButton } from "../components/ShareButton";
import { wrapUpSummary } from "../lib/shareText";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

/**
 * Post-event wrap-up: what actually happened, and how it compares.
 *
 * The recording form is two fields per meal and saves on blur. That constraint
 * is the feature — this gets filled in at the end of a long day or not at all,
 * and half-recorded data is what makes next year's estimate honest.
 */

function money(n) {
  return n == null ? "—" : `$${Number(n).toFixed(2)}`;
}

function ActualsRow({ meal, onSave, canEdit }) {
  const [served, setServed] = useState(meal.served ?? "");
  const [leftover, setLeftover] = useState(meal.leftover ?? "");

  const commit = () => {
    if (served === "" && leftover === "") return;
    onSave(meal.meal_id, {
      served: served === "" ? null : Number(served),
      leftover: leftover === "" ? null : Number(leftover),
    });
  };

  return (
    <Card data-testid={`actuals-row-${meal.meal_id}`}>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between gap-2">
          <div>
            <p className="font-medium text-foreground">{meal.meal_type}</p>
            <p className="text-xs text-muted-foreground">
              Day {meal.day_number} · {meal.planned_quantity} planned
            </p>
          </div>
          {meal.recorded && (
            <span className="text-xs text-emerald-600 font-medium">Recorded</span>
          )}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label htmlFor={`s-${meal.meal_id}`} className="text-xs">Served</Label>
            <Input id={`s-${meal.meal_id}`} type="number" min="0" className="h-11"
                   value={served} disabled={!canEdit}
                   onChange={(e) => setServed(e.target.value)} onBlur={commit}
                   data-testid={`served-${meal.meal_id}`} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={`l-${meal.meal_id}`} className="text-xs">Left over</Label>
            <Input id={`l-${meal.meal_id}`} type="number" min="0" className="h-11"
                   value={leftover} disabled={!canEdit}
                   onChange={(e) => setLeftover(e.target.value)} onBlur={commit}
                   data-testid={`leftover-${meal.meal_id}`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function WrapUpPage() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { authAxios, canEdit } = useAuth();

  const [actuals, setActuals] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [advice, setAdvice] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const api = authAxios();
      const [a, c, q] = await Promise.all([
        api.get(`/events/${eventId}/actuals`),
        api.get(`/events/${eventId}/comparison`),
        api.get(`/events/${eventId}/quantity-advice`),
      ]);
      setActuals(a.data);
      setComparison(c.data);
      setAdvice(q.data);
    } catch {
      toast.error("Could not load the wrap-up");
    } finally {
      setLoading(false);
    }
  }, [authAxios, eventId]);

  useEffect(() => { load(); }, [load]);

  const saveMeal = async (mealId, values) => {
    try {
      const api = authAxios();
      await api.put(`/events/${eventId}/meals/${mealId}/actuals`, values);
      load();
    } catch {
      toast.error("Could not save");
    }
  };

  if (loading) {
    return (
      <div className="p-4 md:p-8 w-full space-y-3">
        <div className="h-24 bg-muted rounded-xl animate-pulse" />
        <div className="h-56 bg-muted rounded-xl animate-pulse" />
      </div>
    );
  }

  const change = comparison?.change;

  return (
    <div className="p-4 md:p-8 w-full space-y-5 pb-24">
      <div className="flex items-center justify-between gap-2">
        <Button variant="ghost" size="sm" className="-ml-2 gap-1.5"
                onClick={() => navigate(`/events/${eventId}`)} data-testid="wrapup-back">
          <ArrowLeft className="h-4 w-4" />
          Event
        </Button>
        <ShareButton getText={() => wrapUpSummary({ actuals, comparison })}
                     title={actuals?.event_title || "Wrap-up"}
                     testId="wrapup-share" />
      </div>

      <div>
        <h1 className="text-xl font-bold text-foreground">Wrap-up</h1>
        <p className="text-sm text-muted-foreground">
          {actuals?.event_title}
        </p>
      </div>

      {/* The advice panel explains itself in words, because a bare number
          invites the reader to ignore it. */}
      {advice && (
        <Card className="border-primary/40" data-testid="quantity-advice">
          <CardContent className="p-5 space-y-2">
            <p className="font-semibold text-foreground flex items-center gap-1.5">
              <Lightbulb className="h-4 w-4" aria-hidden="true" />Quantity advice
            </p>
            <p className="text-2xl font-bold text-foreground">
              {advice.suggested_quantity} portions
            </p>
            <p className="text-sm text-muted-foreground">
              Based on {advice.headcount} people ({advice.basis.replace("_", " ")}),
              with a {advice.buffer_percent}% buffer.
            </p>
            <p className="text-xs text-muted-foreground">{advice.rationale}</p>
          </CardContent>
        </Card>
      )}

      {actuals && (
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-2">
            <p className="font-semibold text-foreground flex items-center gap-1.5">
              <ClipboardCheck className="h-4 w-4" aria-hidden="true" />What actually happened
            </p>
            <span className="text-xs text-muted-foreground" data-testid="actuals-progress">
              {actuals.meals_recorded} of {actuals.meals_total} recorded
            </span>
          </div>

          {actuals.waste_percent != null && (
            <Card data-testid="waste-summary">
              <CardContent className="p-4 grid grid-cols-3 gap-2 text-center">
                {[
                  ["Planned", actuals.total_planned],
                  ["Served", actuals.total_served],
                  ["Left over", actuals.total_leftover],
                ].map(([label, value]) => (
                  <div key={label}>
                    <p className="text-xl font-bold text-foreground">{value}</p>
                    <p className="text-xs text-muted-foreground">{label}</p>
                  </div>
                ))}
                <div className="col-span-3 pt-2 border-t border-border">
                  <p className="text-sm text-muted-foreground flex items-center justify-center gap-1.5">
                    <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                    <strong className="text-foreground">{actuals.waste_percent}%</strong> wasted
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          <div className="space-y-2">
            {actuals.meals.map((meal) => (
              <ActualsRow key={meal.meal_id} meal={meal} onSave={saveMeal} canEdit={canEdit} />
            ))}
            {actuals.meals.length === 0 && (
              <p className="text-sm text-muted-foreground">No meals on this event.</p>
            )}
          </div>
        </div>
      )}

      {comparison?.events?.length > 1 && (
        <div className="space-y-3" data-testid="comparison">
          <p className="font-semibold text-foreground">Compared with previous years</p>

          {change && (
            <Card>
              <CardContent className="p-4 flex items-center gap-3">
                {change.cost_per_head_change > 0
                  ? <TrendingUp className="h-8 w-8 text-destructive" />
                  : <TrendingDown className="h-8 w-8 text-emerald-600" />}
                <div>
                  <p className="font-semibold text-foreground" data-testid="cost-change">
                    {change.cost_per_head_change > 0 ? "+" : ""}
                    {money(change.cost_per_head_change)} per head
                    <span className="text-muted-foreground font-normal">
                      {" "}({change.cost_per_head_change_percent > 0 ? "+" : ""}
                      {change.cost_per_head_change_percent}%)
                    </span>
                  </p>
                  <p className="text-xs text-muted-foreground">
                    vs {change.compared_to} ({change.compared_to_date})
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          <div className="space-y-2">
            {comparison.events.map((e) => (
              <Card key={e.event_id} className={e.is_current ? "border-primary" : ""}
                    data-testid={`comparison-${e.event_id}`}>
                <CardContent className="p-4 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-medium text-foreground truncate">
                      {e.title}
                      {e.is_current && (
                        <span className="text-xs text-primary font-normal"> · this event</span>
                      )}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {e.start_date} · {e.headcount} people ({e.headcount_basis.replace("_", " ")})
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="font-semibold text-foreground">{money(e.cost_per_head)}</p>
                    <p className="text-xs text-muted-foreground">per head</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
